import React, { useState } from "react";
import { Layer, Source, useMap } from "react-map-gl";
import type { Feature } from "geojson";

import { useShouldAutoRefreshWindow } from "../../features/surface/surfaceSlice";
import { useAlertOpacity, useLastReportedAlertUpdateTime } from "../../features/alertDisplay/alertDisplaySlice";

import { WeatherAlertPopup } from "./WeatherAlertPopup";
import { WeatherAlertModal } from "./WeatherAlertModal";

import { useFlashingEffect } from "./hooks/useFlashingEffect";
import { useWeatherAlerts } from "./hooks/useWeatherAlerts";
import { useFilteredAlerts } from "./hooks/useFilteredAlerts";

import { useMapHover } from "./hooks/useMapHover";
import { AnyLayer } from "mapbox-gl";
import { RiskViewerKind, useRiskViewerStateKind } from "../../features/riskViewer/riskViewerSlice";

export default function WeatherAlertsLayer()
{
    const { current: map } = useMap();

    const currentTime = useLastReportedAlertUpdateTime();
    const shouldAutoRefresh = useShouldAutoRefreshWindow();
    const alertOpacity = useAlertOpacity();
    const flashState = useFlashingEffect();
    const riskViewerStateKind = useRiskViewerStateKind();
    const isSelectionState = riskViewerStateKind === RiskViewerKind.SELECTION_STATE;

    const rawAlerts = useWeatherAlerts( shouldAutoRefresh );
    const filteredAlerts = useFilteredAlerts( rawAlerts, currentTime );

    const [ hoveredAlerts, setHoveredAlerts ] = useState<Array<Feature>>( [] );
    const [ modalAlerts, setModalAlerts ] = useState<Array<Feature>>( [] );
    const [ popupCoords, setPopupCoords ] = useState<{ lat: number, lng: number } | null>( null );
    const [ showModal, setShowModal ] = useState( false );

    const handleOpenModal = ( alerts: Array<Feature> ) =>
    {
        setShowModal( true );
        setPopupCoords( null );
        setModalAlerts( alerts );
    };

    const handleCloseModal = () =>
    {
        setShowModal( false );
    };

    useMapHover( {
        map,
        layerId: "weather-alerts-layer",
        setHoveredAlerts,
        setPopupCoords,
        handleOpenModal,
        isSelectionState,
    } );

    const weatherAlertsLayer: AnyLayer = {
        id: "weather-alerts-layer",
        type: "fill",
        source: "weather-alerts",
        paint: {
            "fill-color": [
                "case",
                [ "all", [ "==", [ "get", "isCritical" ], true ], flashState ],
                "white",
                [ "get", "color" ],
            ],
            "fill-opacity": alertOpacity,
            "fill-outline-color": [ "get", "color" ],
        },
    };

    return (
        <React.Fragment>
            <Source id = "weather-alerts" type = "geojson" data = {filteredAlerts}>
                <Layer {...weatherAlertsLayer}/>
            </Source>

            {hoveredAlerts.length > 0 && popupCoords && (
                <WeatherAlertPopup
                    alerts = {hoveredAlerts}
                    lat = {popupCoords.lat}
                    lng = {popupCoords.lng}
                />
            )}

            <WeatherAlertModal alerts = {modalAlerts} open = {showModal} onClose = {handleCloseModal}/>
        </React.Fragment>
    );
}

